// Contract types, transcribed from docs/CONTRACT.md. This file is the
// frontend's copy of the wire shapes: the backend is built in parallel, so
// the contract is the source of truth, not whatever apps/api happens to
// export today.

export type Role = "owner" | "editor" | "viewer";
export type Privacy = "private" | "friends" | "public";
export type Transport = "walk" | "bus" | "train" | "car" | "motorbike" | "bicycle" | "plane";

export const PRIVACY_VALUES: Privacy[] = ["private", "friends", "public"];
export const TRANSPORT_VALUES: Transport[] = [
  "walk",
  "bus",
  "train",
  "car",
  "motorbike",
  "bicycle",
  "plane",
];

export interface User {
  id: string;
  displayName: string;
}

/**
 * The public shape of a traveller, as served by /presence, /explore/moments
 * and /users/:id/timeline. This is a public diary: strangers see each other,
 * so this is the only user shape that ever crosses a trip boundary.
 */
export interface PublicUser {
  id: string;
  username: string;
  displayName: string;
  initials: string;
  /**
   * Stable hash of the user id, the seed the avatar character is drawn from.
   * Falls back to `id` if an older server build has not sent it yet.
   */
  avatarSeed?: string;
  /**
   * Server side hue. Kept on the wire, deliberately NOT painted: the avatar
   * is flat black ink on white and a coloured disc behind it would add chroma
   * the palette does not allow.
   */
  avatarHue?: number;
  /**
   * A real uploaded photo, when the traveller replaced their drawn character
   * with one. Present on every public row so a face never has to be fetched
   * separately. `bio` deliberately is NOT here: it is up to 300 characters
   * and only GET /users/me carries it, so it does not ride on every feed row.
   */
  photoUrl?: string | null;
}

/** One live traveller on the public map. Not scoped to a trip. */
export interface LivePresence {
  user: PublicUser;
  lat: number;
  lng: number;
  accuracyM: number | null;
  tripId: string | null;
  lastSeenAt: string;
}

export interface PresenceInput {
  lat: number;
  lng: number;
  accuracyM?: number | null;
  tripId?: string | null;
}

/**
 * A moment plus the traveller who wrote it and its social counters, the shape
 * the public feed, a traveller timeline and a place lookup all return.
 * Counters are optional so the UI still renders against an API build that has
 * not shipped likes and comments yet.
 */
export interface AuthoredMoment extends Moment {
  author: PublicUser;
  tripTitle: string | null;
  likeCount?: number;
  commentCount?: number;
  likedByMe?: boolean;
}

/**
 * A destination. Per the round 3 addendum a map pin is a PLACE, not a person:
 * several independent travellers post diaries at the same coordinate and one
 * pin carries all of them.
 */
export interface Place {
  /** Stable, derived from the rounded coordinate. Safe as a React key. */
  id: string;
  lat: number;
  lng: number;
  placeName: string | null;
  /** EXACT distinct author count, even when `travellers` is truncated. */
  travellerCount: number;
  momentCount: number;
  /** Capped at 8 by the contract, most recent first. */
  travellers: PublicUser[];
  lastMomentAt: string;
}

export interface PlaceEntries {
  place: Place;
  entries: AuthoredMoment[];
}

/** One address search hit from GET /geocode. */
export interface GeocodeHit {
  label: string;
  lat: number;
  lng: number;
  kind?: string | null;
}

export interface GeocodeResult {
  results: GeocodeHit[];
  /** Provider credit, displayed the same way as the map attribution. */
  attribution: string | null;
}

/** Plain text, always rendered as text. Never as HTML, this is public input. */
export interface Comment {
  id: string;
  momentId: string;
  author: PublicUser;
  body: string;
  createdAt: string;
}

export interface Trip {
  id: string;
  ownerId: string;
  title: string;
  description: string | null;
  startDate: string;
  endDate: string | null;
  transportModes: Transport[];
  coverMediaKey: string | null;
  privacy: Privacy;
  createdAt: string;
  updatedAt: string;
}

export interface TripMember {
  userId: string;
  role: Role;
  displayName?: string;
}

export interface TripStats {
  momentCount?: number;
  trackPointCount?: number;
  distanceM?: number;
  dayCount?: number;
}

export interface TripDetail {
  trip: Trip;
  members: TripMember[];
  stats: TripStats;
}

export interface TrackPointInput {
  lat: number;
  lng: number;
  accuracyM?: number | null;
  altitudeM?: number | null;
  speedMps?: number | null;
  recordedAt: string;
}

export interface TrackPoint extends TrackPointInput {
  id: string;
  tripId: string;
  userId: string;
}

export interface MomentMedia {
  id: string;
  storageKey: string;
  mime: string;
  width: number | null;
  height: number | null;
  position: number;
}

export interface MomentMediaInput {
  storageKey: string;
  mime: string;
  width?: number | null;
  height?: number | null;
}

export interface MomentInput {
  clientId: string;
  lat: number | null;
  lng: number | null;
  placeName?: string | null;
  caption: string;
  capturedAt: string;
  privacy?: Privacy;
  media: MomentMediaInput[];
}

export interface Moment {
  id: string;
  tripId: string;
  authorId: string;
  clientId: string;
  lat: number | null;
  lng: number | null;
  placeName: string | null;
  caption: string;
  capturedAt: string;
  privacy: Privacy;
  media: MomentMedia[];
  createdAt: string;
  updatedAt: string;
}

export interface MediaUploadResult {
  key: string;
  url: string;
  mime: string;
  width?: number | null;
  height?: number | null;
}

export interface CreateTripInput {
  title: string;
  startDate: string;
  endDate?: string | null;
  description?: string | null;
  transportModes?: Transport[];
}

/**
 * The moment frames are served on two topics with one shape family: the trip
 * topic carries `tripId`, the public `feed:global` topic carries the authored
 * moment and its place. Modelling them as one member each keeps the frame
 * handlers from having to guess which variant they were handed.
 */
export type RealtimeFrame =
  | { type: "track.appended"; tripId: string; points: TrackPoint[] }
  | { type: "moment.created"; tripId?: string; moment: Moment | AuthoredMoment; place?: Place }
  | { type: "moment.updated"; tripId?: string; moment: Moment | AuthoredMoment; place?: Place }
  | { type: "moment.deleted"; tripId?: string; momentId: string; place?: Place }
  | { type: "presence"; tripId: string; userIds: string[] }
  | { type: "presence.moved"; presence: LivePresence }
  | { type: "presence.left"; userId: string }
  | { type: "like.changed"; momentId: string; likeCount: number }
  | { type: "comment.created"; comment: Comment };

/** Narrowing helper: did this moment frame arrive with its author attached. */
export function isAuthoredMoment(moment: Moment | AuthoredMoment): moment is AuthoredMoment {
  return Boolean((moment as AuthoredMoment).author?.id);
}

/* -------------------------------------------------- round 5: discovery */

/**
 * DUMMY DATA for this prototype. There is no pricing source behind these
 * numbers: the seeded ranges are plausible placeholders. Every surface that
 * shows one must label it as an estimate and must never present it as a
 * real price. Recorded in the contract for the same reason.
 */
export interface BudgetEstimate {
  currency: string;
  /** Per person, whole trip to that destination. */
  min: number;
  max: number;
  /** What the range covers, when the server says. */
  note: string | null;
}

/** One card in the Cari deck. A place with at least one public photo. */
export interface DiscoverCard {
  placeId: string;
  name: string;
  lat: number;
  lng: number;
  region: string | null;
  media: MomentMedia[];
  travellerCount: number;
  budget: BudgetEstimate | null;
  summary: string | null;
}

/** A saved destination. Private to its owner, never on a public surface. */
export interface WishlistItem {
  placeId: string;
  name: string;
  lat: number;
  lng: number;
  media: MomentMedia[];
  budget: BudgetEstimate | null;
  addedAt: string;
}

export type SwipeDirection = "left" | "right";

export type SearchType = "all" | "place" | "traveller" | "moment";

export const SEARCH_TYPES: SearchType[] = ["all", "place", "traveller", "moment"];

export interface SearchResults {
  places: Place[];
  travellers: PublicUser[];
  moments: AuthoredMoment[];
  nextCursor?: string | null;
}

/**
 * The signed in traveller's own record: the public shape plus the fields
 * only the owner may read and edit.
 */
export interface MeUser extends PublicUser {
  /** Owner only. Never present on a PublicUser in a feed or a place lookup. */
  bio?: string | null;
}

export interface ProfilePatch {
  displayName?: string;
  bio?: string;
  /**
   * Identity, NOT derived from the name. Sent only when the traveller
   * deliberately regenerates their character, never as a side effect of an
   * edit to displayName.
   */
  avatarSeed?: string;
}

/* ------------------------------------------------- round 7: itinerary */

/**
 * One planned stop on one day of a trip. A plan item is NOT a diary entry:
 * it is what the traveller intends, written before or after the fact, and it
 * lives alongside the entries that day rather than replacing them.
 */
export interface ItineraryInput {
  /** ISO date, inside the trip range. */
  date: string;
  /** Links to an existing destination when the traveller picked one. */
  placeId?: string | null;
  placeName: string;
  lat?: number | null;
  lng?: number | null;
  note?: string | null;
  /**
   * A SORT KEY, not an index. It is a double, and the list is never
   * renumbered: a new stop takes the last position on its day plus a step,
   * and a future drag would take the midpoint between its two new
   * neighbours. Order is (date, position, createdAt, id).
   */
  position: number;
}

export interface ItineraryItem extends ItineraryInput {
  id: string;
  tripId: string;
  placeId: string | null;
  lat: number | null;
  lng: number | null;
  note: string | null;
  createdAt?: string;
  updatedAt?: string;
}

/* ---------------------------------------------------- round 7: budget */

export type BudgetCategory =
  | "transport"
  | "lodging"
  | "food"
  | "activity"
  | "shopping"
  | "other";

export const BUDGET_CATEGORIES: BudgetCategory[] = [
  "transport",
  "lodging",
  "food",
  "activity",
  "shopping",
  "other",
];

/**
 * Money is an INTEGER in minor units, never a float. A float amount is a
 * rounding bug waiting to be found by a traveller counting their own cash,
 * so nothing in this app ever parses a typed amount with parseFloat.
 */
export interface BudgetInput {
  category: BudgetCategory;
  label: string;
  amountMinor: number;
  /** ISO 4217. */
  currency: string;
  /** ISO date. */
  spentAt: string;
  note?: string | null;
}

export interface BudgetItem extends BudgetInput {
  id: string;
  tripId: string;
  note: string | null;
  createdAt?: string;
  updatedAt?: string;
}

/**
 * Totals are reported PER CURRENCY. Two currencies are never added into one
 * number: an exchange rate the app does not have is not a number it may
 * invent. The server sends `byCategory` as an array of buckets; the client
 * normalises it to a lookup in lib/api.ts so screens read one shape.
 */
export interface BudgetTotals {
  currency: string;
  totalMinor: number;
  itemCount: number;
  byCategory: Partial<Record<BudgetCategory, number>>;
}

export interface BudgetSummary {
  items: BudgetItem[];
  totals: BudgetTotals[];
}

/* ----------------------------------------------------- round 7: stats */

/**
 * Every field is nullable ON PURPOSE. `null` means the server could not
 * derive the figure from real history, and the UI renders it as unavailable.
 * Zero is a claim, absence is not, and showing one as the other is a lie
 * about a traveller's own record.
 */
/** One day in the calendar read model. A view over trips and moments. */
export interface CalendarDay {
  date: string;
  tripIds: string[];
  entryCount: number;
  entryIds: string[];
}

export interface CalendarMonth {
  from: string;
  to: string;
  days: CalendarDay[];
  trips: Trip[];
}

export interface TravelStats {
  entries: number | null;
  destinations: number | null;
  trips: number | null;
  daysTravelled: number | null;
  regions: string[] | null;
  provinces: string[] | null;
  totalDistanceM: number | null;
  farthestFromHomeM: number | null;
}
